import { CalendarClock, CalendarX } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { formatBusinessDate } from '@/lib/datetime'

import type { PostingDay } from './use-posting-day'

/**
 * What the business day means for the screen the operator is looking at.
 *
 * It renders only when there is something to say. A branch working in an open day needs no banner —
 * the header already carries the date — but a branch with no day open, or a day that is closing,
 * has to be told before an amount is keyed rather than after the customer has handed over cash.
 */
export function PostingDayNotice({ day }: { day: PostingDay }) {
  if (day.isPending) {
    return <Skeleton className="h-16 w-full" />
  }

  if (day.isError) {
    return (
      <Alert tone="warning">
        <CalendarX aria-hidden />
        <div className="space-y-1">
          <AlertTitle>The business day could not be read</AlertTitle>
          <AlertDescription>
            Nothing on this screen may be posted until the ledger answers with the branch&apos;s
            date. The date is the ledger&apos;s, not this machine&apos;s, so there is nothing to fall
            back to.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  if (day.businessDate === null) {
    return (
      <Alert tone="warning">
        <CalendarX aria-hidden />
        <div className="space-y-1">
          <AlertTitle>No business day is open at this branch</AlertTitle>
          <AlertDescription>
            Nothing posts until someone with <span className="font-mono text-xs">ledger.day.open</span>{' '}
            opens the day. This is the ordinary state first thing in the morning.
            {day.lastClosedBusinessDate ? (
              <> The branch last traded on {formatBusinessDate(day.lastClosedBusinessDate)}.</>
            ) : null}
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  if (!day.acceptsPostings) {
    return (
      <Alert tone="warning">
        <CalendarClock aria-hidden />
        <div className="space-y-1">
          <AlertTitle>{formatBusinessDate(day.businessDate)} is not taking postings</AlertTitle>
          <AlertDescription>
            The day is {day.status === 'Closing' ? 'closing' : 'not open for postings'}: end-of-day
            checks are running and the ledger accepts nothing new into it. Counts and reads still
            work — cash cannot be moved.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return null
}
